import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import * as _ from 'lodash';
import { BehaviorSubject, Subject, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { CuttingMatBaseFurniture } from '../../models/cutting/cutting-mat-base-furniture.model';
import { CuttingMatBaseGroup } from '../../models/cutting/cutting-material-group.model';
import { CuttingOrderFurniture } from '../../models/cutting/cutting-order-furniture.model';
import { CuttingMaterialBaseService } from '../../services/cutting-material-base.service';
import { ImageViewerComponent, ImageViewerData } from '../image-viewer/image-viewer.component';

@Component({
  templateUrl: './cutting-furniture-base-dialog.component.html',
  styleUrls: ['./cutting-furniture-base-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [CuttingMaterialBaseService],
})
export class CuttingFurnitureBaseDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  imageViewerDialogRef: MatDialogRef<ImageViewerComponent>;
  dataSource: MatTableDataSource<CuttingMatBaseFurniture> | null;
  displayedColumns = ['select', 'texture', 'name', 'article', 'price', 'quantityInStock'];
  unsubscribe$: Subject<void> = new Subject();
  selection = new SelectionModel<number>(true, []);
  selected: CuttingMatBaseFurniture[] = [];
  loadMore$: BehaviorSubject<[number, number]> = new BehaviorSubject([0, 25]);
  loading = false;

  @Input() currencySymbol = '';
  @Input() showMaterialsPrice = false;
  @Input() showQuantityInStock = false;

  @ViewChild('filter', { static: true }) filter: ElementRef;
  @ViewChild('filterButton', { static: true }) filterButton: MatButton;

  currentGroupId = 0;
  focusedGroupId = -1;
  furnitures: CuttingMatBaseFurniture[] = [];

  // selectedFurnitures: CuttingMatBaseFurniture[] = [];

  get furnitureGroups(): CuttingMatBaseGroup[] {
    const groups = this.materialBaseService.onFurnitureGroupsChanged.value;
    if (groups.length > 0) {
      this.focusedGroupId = groups[0].id;
    }
    return this.materialBaseService.onFurnitureGroupsChanged.value;
  }

  get hasMore(): boolean {
    return this.materialBaseService.furnituresTotlaCount - this.dataSource.data.length > 0;
  }

  constructor(
    public dialogRef: MatDialogRef<CuttingFurnitureBaseDialogComponent>,
    public materialBaseService: CuttingMaterialBaseService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource();
    this.getFurnitureGroups();
    if (!this.showMaterialsPrice) {
      _.pull(this.displayedColumns, 'price');
    }

    if (!this.showQuantityInStock) {
      _.pull(this.displayedColumns, 'quantityInStock');
    }
  }

  ngAfterViewInit(): void {
    this.addListners();
  }

  onGroupSelect(group: CuttingMatBaseGroup): void {
    this.currentGroupId = group.id;
    this.dataSource.data = [];
    this.loadMore$.next([0, 25]);
  }

  private addListners(): void {
    const filterEvent = merge(
      fromEvent(this.filter.nativeElement, 'keyup'), //
      fromEvent(this.filterButton._elementRef.nativeElement, 'click')
    ).pipe(debounceTime(200), distinctUntilChanged());

    this.loadMore$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.getFurnitures();
    });

    filterEvent.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.dataSource.data = [];
      this.loadMore$.next([0, 25]);
    });

    this.materialBaseService.onFurnitureListChanged //
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response: CuttingMatBaseFurniture[]) => {
        this.loading = false;
        this.dataSource.data.push(...response);
        this.dataSource.data = this.dataSource.data;
      });
  }

  toggleSelected(item: CuttingOrderFurniture): void {
    this.selection.toggle(item.id);
    const hasIndex = this.selected.findIndex(f => f.id === item.id);

    if (hasIndex < 0) {
      this.selected.push(item);
    } else {
      this.selected.splice(hasIndex, 1);
    }
  }

  private getFurnitures(): void {
    this.filter.nativeElement.value = (this.filter.nativeElement.value as string).replace(/'/g, '');

    this.materialBaseService.getFurnitures(
      this.currentGroupId,
      this.loadMore$.value[1],
      this.loadMore$.value[0],
      this.filter.nativeElement.value
    );
  }

  getFurnitureGroups(): void {
    this.materialBaseService.getFurnitureGroups();
  }

  setImagePlug(e: Event): void {
    (e.target as HTMLImageElement).src = '';
  }

  saveAndClose(): void {
    this.dialogRef.close(this.selected);
  }

  openTextureViewerDialog(link: string): void {
    this.imageViewerDialogRef = this.dialog.open(ImageViewerComponent, {
      panelClass: 'image-viewer-dialog',
      disableClose: false,
      data: {
        textureLink: link,
      } as ImageViewerData,
    });

    this.imageViewerDialogRef.afterClosed().subscribe(() => {
      this.imageViewerDialogRef = null;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
